import { useMemo, useState } from 'react'
import Button from 'antd/lib/button'
import { PanelHeader } from 'Components/common/TaskPanelHeader/TaskPanelHeader'
import LiveFeedbackIcon from 'Assets/icons/live-feedback.svg'
import { CircleLoader } from 'shared/components/CircleLoader/CircleLoader'
import Tooltip from 'antd/lib/tooltip'
import classes from 'Plugins/LiveFeedback/component/LiveFeedback.module.less'
import { LiveFeedbackDetails } from './LiveFeedbackDetails/LiveFeedbackDetails'
import { DisabledStateType, LiveFeedbackDetailsType } from 'Types/LiveFeedback.types'
import { ParameterFeedback } from './ParameterFeedback/ParameterFeedback'
import { OverallFeedback } from './OverallFeedback/OverallFeedback'

export type LiveFeedbackProps = {
    onLiveFeedback: () => Promise<void>
    getLiveFeedbackDetails: () => LiveFeedbackDetailsType[] | undefined
    getDisabledState: () => DisabledStateType
    setParameterFeedback: (parameterId: string, isAccepted: boolean) => void
    setOverallFeedback: (overallFeedback: string) => void
    getOverallFeedback: () => string
    getParameterFeedback: () => Record<string, boolean>
    isViolationHighlighted: () => boolean
}

export const LiveFeedbackComponent: React.FC<LiveFeedbackProps> = (props: LiveFeedbackProps) => {
    const [isLoading, setIsLoading] = useState(false)
    const feedbackDetails = props.getLiveFeedbackDetails()
    const [activeIndex, setActiveIndex] = useState(0)

    const handleCollapseChange = (key: string | string[]) => {
        setActiveIndex(Array.isArray(key) ? Number(key[0]) : Number(key))
    }

    const activeDetail = feedbackDetails?.[activeIndex] ?? null

    const mode: 'loading' | 'details' | 'hint' = useMemo(() => {
        if (isLoading) {
            return 'loading'
        }
        if (feedbackDetails) {
            return 'details'
        }
        return 'hint'
    }, [isLoading, feedbackDetails])

    const content = useMemo(() => {
        switch (mode) {
            case 'loading':
                return (
                    <div className={classes.loadingContainer}>
                        <CircleLoader size={28} />
                        <div>Scanning...Please wait...</div>
                    </div>
                )
            case 'hint':
                return (
                    <div className={classes.hintText}>
                        Click on “Get Live Feedback” to check your response.
                    </div>
                )
            case 'details':
                return (
                    <LiveFeedbackDetails
                        details={feedbackDetails}
                        onCollapseChange={handleCollapseChange}
                        isViolationHighlighted={props.isViolationHighlighted()}
                        parameterFeedback={props.getParameterFeedback()}
                    />
                )
            default:
                return null
        }
    }, [feedbackDetails, mode, props])

    return (
        <div className={classes.liveFeedbackRoot}>
            <PanelHeader logo={LiveFeedbackIcon} title={'Live Feedback'} />
            <div className={classes.body}>{content}</div>
            {!!activeDetail && !isLoading && (
                <ParameterFeedback
                    serialNumber={activeIndex + 1}
                    totalCount={feedbackDetails?.length ?? 0}
                    activeDetail={activeDetail}
                    parameterFeedback={props.getParameterFeedback()}
                    onFeedback={props.setParameterFeedback}
                />
            )}
            <div
                className={
                    classes.footer +
                    ' ' +
                    (feedbackDetails?.length ? classes.footerWithOverallFeedback : '')
                }
            >
                <Tooltip trigger="hover" title={props.getDisabledState().reason}>
                    <Button
                        className={classes.scanButton}
                        onClick={async () => {
                            setIsLoading(true)
                            await props.onLiveFeedback()
                            setIsLoading(false)
                        }}
                        disabled={isLoading || props.getDisabledState().isDisabled}
                    >
                        {feedbackDetails ? 'Re-run plugin' : 'Get Live Feedback'}
                    </Button>
                </Tooltip>
                {feedbackDetails?.length ? (
                    <OverallFeedback
                        overallFeedback={props.getOverallFeedback()}
                        setOverallFeedback={props.setOverallFeedback}
                        disabled={isLoading}
                    />
                ) : null}
            </div>
        </div>
    )
}
